import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { EventStatusBadge } from '../components/events/EventStatusBadge.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventItem {
  id: string;
  name: string;
  status: string;
  startsAt: string;
  endsAt: string | null;
  locationName: string | null;
  maxCapacity: number | null;
  confirmedCount?: number;
  waitlistedCount?: number;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'draft', label: 'Bozza' },
  { value: 'published', label: 'Pubblicato' },
  { value: 'cancelled', label: 'Annullato' },
  { value: 'completed', label: 'Completato' },
];

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EventsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [statusFilter, setStatusFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<EventItem | null>(null);

  const { data = [], isLoading } = useQuery<EventItem[]>({
    queryKey: ['events', statusFilter],
    queryFn: () =>
      api
        .get('/api/v1/events', {
          params: statusFilter ? { status: statusFilter } : undefined,
        })
        .then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/events/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      setDeleteTarget(null);
      toast.success('Event deleted');
    },
    onError: (err) => toast.error(extractError(err, 'Error deleting event')),
  });

  return (
    <div>
      <PageHeader
        title="Events"
        action={
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-zinc-200 rounded-lg px-3 py-1.5 text-zinc-700 bg-white focus:outline-none focus:ring-2 focus:ring-zinc-300"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Button onClick={() => navigate('/events/new')} size="sm">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Event
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={6} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<CalendarDays size={26} />}
          title="No events found"
          description="Create your first event to get started"
          action={{ label: 'New Event', onClick: () => navigate('/events/new') }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Start
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Capacity
                </th>
                <th className="px-4 py-3 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((ev) => (
                <tr
                  key={ev.id}
                  className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/events/${ev.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{ev.name}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{formatDate(ev.startsAt)}</td>
                  <td className="px-4 py-3 text-zinc-500">{ev.locationName ?? '—'}</td>
                  <td className="px-4 py-3">
                    <EventStatusBadge status={ev.status} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-700">
                    {ev.maxCapacity ?? '∞'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(ev);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete event?"
        message={`"${deleteTarget?.name}" will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
